import { PropType } from 'vue';
import { mapGetters } from 'vuex';
import Scene from './Scene/Scene.vue';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import { isDefaultScene } from '@/core/utils/utils';
import {
  SceneDTO,
  PlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import DialogConfirmDeleteScene from './DialogConfirmDeleteScene/DialogConfirmDeleteScene.vue';
import DialogConfirmDisableScene from './DialogConfirmDisableScene/DialogConfirmDisableScene.vue';

@Options({
  name: 'PlantScenes',
  emits: ['add-object', 'add-structure', 'update-list'],
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  components: {
    Scene,
    DialogConfirmDeleteScene,
    DialogConfirmDisableScene,
  },
  computed: {
    ...mapGetters('UI', ['scenesActionsEnabled', 'scenesAddSceneEnabled']),
  },
})
export default class PlantScenes extends Vue {
  currentPlant!: PlantDetailDTO;
  scenes: SceneDTO[] = [];

  isVisibleDeleteConfirm = false;
  isVisibleDisableConfirm = false;
  tempScene: SceneDTO | null = null;

  get backLink() {
    return {
      name: 'page-plant-dashboard',
      params: this.$route.params,
    };
  }

  get sceneCategories() {
    let defaultScenes: SceneDTO[] = [];
    let customScenes: SceneDTO[] = [];

    this.scenes.forEach((s) => {
      if (isDefaultScene(s)) {
        defaultScenes.push(s);
      } else {
        customScenes.push(s);
      }
    });

    return {
      defaultScenes,
      customScenes,
    };
  }

  mounted() {
    this.fetchData();
  }

  async fetchData() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.scenes = await plantsClient.scenesAll(
        this.currentPlant.desktopId as number
      );
    } catch (error) {
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleCreateScene() {
    this.$router.push({
      name: 'page-scene-wizard',
      params: {
        sceneIndex: this.sceneCategories.customScenes.length,
      },
    });
  }

  handleEditScene(s: SceneDTO) {
    this.$router.push({
      name: 'page-scene-wizard',
      params: {
        id: s.id as string,
      },
    });
  }

  async handleDuplicateScene(s: SceneDTO) {
    this.$store.dispatch(SET_APP_LOADING, true);

    try {
      await plantsClient.cloneScene(
        this.currentPlant.desktopId as number,
        s.id as string
      );

      this.$nextTick(() => {
        this.fetchData();
      });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  toggleDeleteConfirm(s: SceneDTO) {
    this.isVisibleDeleteConfirm = !this.isVisibleDeleteConfirm;
    this.tempScene = this.isVisibleDeleteConfirm ? s : null;
  }

  toggleDisableConfirm(s: SceneDTO) {
    this.isVisibleDisableConfirm = !this.isVisibleDisableConfirm;
    this.tempScene = this.isVisibleDisableConfirm ? s : null;
  }

  async handleDoDeleteScene() {
    if (!this.tempScene) {
      return;
    }

    this.$store.dispatch(SET_APP_LOADING, true);

    try {
      await plantsClient.scenesDELETE(
        this.currentPlant.desktopId as number,
        this.tempScene?.id as string
      );

      this.fetchData();
    } catch (error) {
      let messageError = '';
      console.log(error);
      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async handleToggleEnabledScene(s?: SceneDTO) {
    if (s) {
      this.tempScene = s;
    }

    this.$store.dispatch(SET_APP_LOADING, true);

    try {
      await plantsClient.toggleDisable(
        this.currentPlant.desktopId as number,
        this.tempScene?.id as string
      );

      this.$nextTick(() => {
        this.fetchData();
      });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }

    if (s) {
      this.tempScene = null;
    }
  }
}
